import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Phase } from '../lib/api';
import {
  CheckCircleIcon,
  RocketLaunchIcon,
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ArrowPathIcon,
  SparklesIcon,
  StarIcon,
  PhoneIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';

export default function ActivationPage() {
  const navigate = useNavigate();
  const [phases, setPhases] = useState<Phase[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isLaunching, setIsLaunching] = useState(false);
  const [launched, setLaunched] = useState(false);
  const [launchMode, setLaunchMode] = useState<'export' | 'evergreen' | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const phaseData = await api.listPhases();
      setPhases(phaseData.phases);
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const CHECKLIST_PHASES = [
    { label: 'Business Profile', slug: 'business-profile', number: 1 },
    { label: 'Business Intelligence', slug: 'business-intel', number: 2 },
    { label: 'Brand Guide', slug: 'brand-guide', number: 3 },
    { label: 'Ideal Client Profiles', slug: 'icp', number: 4 },
    { label: 'Images & Assets', slug: 'assets', number: 5 },
    { label: 'Website & Funnel', slug: 'website', number: 6 },
    { label: 'Blog Content', slug: 'blog', number: 7 },
    { label: 'Social Media', slug: 'social', number: 8 },
    { label: 'Email & SMS', slug: 'messaging', number: 9 },
  ];

  const checklist = CHECKLIST_PHASES.map(item => {
    const phase = phases.find(p => p.slug === item.slug);
    return {
      label: item.label,
      phaseSlug: item.slug,
      phaseNumber: item.number,
      done: phase?.status === 'completed',
    };
  });

  const allComplete = checklist.every(i => i.done);
  const completedCount = checklist.filter(i => i.done).length;

  const handleLaunch = async (mode: 'export' | 'evergreen') => {
    setLaunchMode(mode);
    setIsLaunching(true);
    try {
      if (mode === 'export') {
        const blob = await api.exportContent();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'content-package.zip';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setLaunched(true);
      } else {
        window.open('https://www.happycamperproductions.com/trailhead-signup', '_blank');
        setIsLaunching(false);
        return;
      }
    } catch (error) {
      console.error('Launch failed:', error);
    } finally {
      setIsLaunching(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-copper-200 border-t-copper-500" />
      </div>
    );
  }

  /* ── Post-launch success ── */
  if (launched) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 space-y-6 px-4">
        <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-sage-400 to-sage-600 flex items-center justify-center shadow-lg">
          <RocketLaunchIcon className="h-12 w-12 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-charcoal-700">You're Live! 🎉</h1>
        <p className="text-charcoal-400 text-lg max-w-md mx-auto leading-relaxed">
          {launchMode === 'evergreen'
            ? "Welcome to Evergreen! Your content is being deployed now. You'll receive an email with login details and your first optimization report within 24 hours."
            : "Your complete marketing package is being prepared for download. You'll receive an email with your asset files shortly."
          }
        </p>
        {launchMode === 'evergreen' && (
          <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 max-w-md mx-auto">
            <p className="text-sage-700 text-sm font-medium">🎁 Your first month is just $1. Full pricing starts month 2.</p>
          </div>
        )}
        <button onClick={() => navigate('/')} className="btn-primary inline-flex items-center mt-4">
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12 px-4">
      {/* Back */}
      <button
        onClick={() => navigate('/')}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cream-100 text-charcoal-500 hover:bg-copper-100 hover:text-copper-700 transition-colors text-sm font-medium border border-charcoal-100"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Your Journey
      </button>

      {/* Hero Header — The Summit */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl">
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal-800 via-teal-800 to-charcoal-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(196,117,76,0.2),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(90,123,111,0.15),transparent_60%)]" />

        <div className="absolute bottom-0 left-0 right-0 h-20 opacity-10">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full fill-white">
            <path d="M0,120 L0,80 L150,30 L300,65 L450,15 L600,55 L750,5 L900,45 L1050,20 L1200,60 L1200,120 Z" />
          </svg>
        </div>

        <div className="absolute top-6 right-8 hidden md:block">
          <SparklesIcon className="h-8 w-8 text-copper-400/30 animate-pulse" />
        </div>
        <div className="absolute top-16 right-24 hidden md:block">
          <StarIcon className="h-5 w-5 text-sage-400/25 animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative px-6 py-8 md:px-10 md:py-10">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-4">
            <div className="w-2 h-2 rounded-full bg-copper-400 animate-pulse" />
            <span className="text-xs font-semibold text-copper-300 uppercase tracking-widest">Final Phase</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            {allComplete ? (
              <>You've reached the <span className="text-transparent bg-clip-text bg-gradient-to-r from-copper-300 to-copper-500">summit</span>.</>
            ) : (
              <>The summit is <span className="text-transparent bg-clip-text bg-gradient-to-r from-copper-300 to-copper-500">in sight</span>.</>
            )}
          </h1>
          <p className="mt-2 text-base text-white/60 max-w-xl leading-relaxed">
            {allComplete
              ? "Your entire marketing system is built, reviewed, and ready. Choose how you want to launch."
              : "You're almost there. Complete the remaining phases, then choose how to deploy your marketing system."
            }
          </p>

          <div className="mt-6 flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              {checklist.map((item, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full transition-all duration-500 ${
                    item.done
                      ? 'bg-gradient-to-br from-copper-400 to-sage-500 shadow-sm shadow-copper-400/30'
                      : 'bg-white/15 border border-white/10'
                  }`}
                  title={item.label}
                />
              ))}
            </div>
            <span className="text-white/40 text-sm ml-2">{completedCount}/{checklist.length} phases complete</span>
          </div>
        </div>
      </div>

      {/* ── Deployment Options ── */}
      {allComplete && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-charcoal-700">Choose Your Launch Path</h2>
            <p className="text-charcoal-400 mt-1">You've built an incredible marketing system. Now decide how to put it to work.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* ── Option 1: Export (Basic) ── */}
            <div className="bg-white rounded-2xl border border-charcoal-200 shadow-sm p-6 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-charcoal-100 rounded-xl">
                  <ArrowDownTrayIcon className="h-6 w-6 text-charcoal-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-charcoal-700">Export Package</h3>
                  <p className="text-sm text-charcoal-400">DIY deployment</p>
                </div>
              </div>
              <p className="text-charcoal-500 text-sm leading-relaxed mb-4">
                Download all your generated content as files — website HTML, blog posts, social media text, email templates, and images. Deploy them yourself or hand them to your webmaster.
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm text-charcoal-500">
                  <CheckCircleIcon className="h-4 w-4 text-sage-500 flex-shrink-0" />
                  All content in downloadable formats
                </li>
                <li className="flex items-center gap-2 text-sm text-charcoal-500">
                  <CheckCircleIcon className="h-4 w-4 text-sage-500 flex-shrink-0" />
                  Website HTML & CSS files
                </li>
                <li className="flex items-center gap-2 text-sm text-charcoal-500">
                  <CheckCircleIcon className="h-4 w-4 text-sage-500 flex-shrink-0" />
                  Blog posts in Markdown & HTML
                </li>
                <li className="flex items-center gap-2 text-sm text-charcoal-500">
                  <CheckCircleIcon className="h-4 w-4 text-sage-500 flex-shrink-0" />
                  Image library with all assets
                </li>
              </ul>
              <div className="text-center">
                <p className="text-sm text-charcoal-400 mb-3">Included with your plan</p>
                <button
                  onClick={() => handleLaunch('export')}
                  disabled={isLaunching}
                  className="w-full py-3 px-6 border-2 border-charcoal-200 text-charcoal-700 font-semibold rounded-xl hover:bg-charcoal-50 transition-colors disabled:opacity-50"
                >
                  {isLaunching && launchMode === 'export' ? 'Preparing...' : 'Download Export Package'}
                </button>
              </div>
            </div>

            {/* ── Option 2: Evergreen (Premium) ── */}
            <div className="relative bg-white rounded-2xl border-2 border-copper-400 shadow-lg p-6 flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-copper-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-sm">
                  RECOMMENDED
                </span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-copper-100 rounded-xl">
                  <BoltIcon className="h-6 w-6 text-copper-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-charcoal-700">Evergreen Machine</h3>
                  <p className="text-sm text-copper-600 font-medium">Fully managed deployment</p>
                </div>
              </div>
              <p className="text-charcoal-500 text-sm leading-relaxed mb-4">
                We don't just build your marketing — we <strong>run it for you</strong>. Evergreen publishes your content on schedule, keeps your website updated, and optimizes your campaigns based on real performance data.
              </p>
              <ul className="space-y-2 mb-4 flex-1">
                <li className="flex items-start gap-2.5 text-sm text-charcoal-600">
                  <CheckCircleIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                  <span>Auto-publish blog posts & social content on schedule</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-charcoal-600">
                  <CheckCircleIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                  <span>Website hosting with SSL, CDN, and analytics</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-charcoal-600">
                  <CheckCircleIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                  <span>Email & SMS automation sent on your behalf</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-charcoal-600">
                  <CheckCircleIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                  <span>Monthly content refresh with seasonal campaigns</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-charcoal-600">
                  <CheckCircleIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                  <span>Performance dashboard with traffic & conversion tracking</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-charcoal-600">
                  <CheckCircleIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                  <span>AI optimization that adjusts what's working</span>
                </li>
              </ul>

              {/* Bonus incentives */}
              <div className="bg-gradient-to-r from-copper-50 to-cream-100 border border-copper-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-bold text-copper-700 uppercase tracking-wide mb-2">🎁 Launch Bonuses Included</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <PhoneIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-charcoal-600"><strong className="text-charcoal-700">Phone & Texting AI</strong> — an AI receptionist programmed for your business, handling calls and texts 24/7</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MegaphoneIcon className="h-4 w-4 text-copper-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-charcoal-600"><strong className="text-charcoal-700">Paid Ad Starter Kit</strong> — ready-to-launch ad campaigns to start driving traffic to your business immediately</span>
                  </div>
                </div>
              </div>

              <div className="text-center space-y-3">
                <div>
                  <p className="text-3xl font-bold text-copper-500">$497<span className="text-base font-normal text-charcoal-400">/mo</span></p>
                  <p className="text-sm text-sage-700 font-semibold mt-1">🎁 First month just $1 — start today</p>
                </div>
                <button
                  onClick={() => handleLaunch('evergreen')}
                  disabled={isLaunching}
                  className="w-full py-3 px-6 bg-copper-500 text-white font-semibold rounded-xl hover:bg-copper-600 transition-all shadow-lg shadow-copper-500/25 hover:shadow-xl hover:shadow-copper-500/30 disabled:opacity-50"
                >
                  {isLaunching && launchMode === 'evergreen' ? 'Activating...' : 'Start for $1'}
                </button>
                <p className="text-xs text-charcoal-400">Cancel anytime · $497/mo starts month 2</p>
              </div>
            </div>
          </div>

          {/* ── Why Evergreen — Feature Detail ── */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 md:p-8 text-white shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">Why business owners choose Evergreen</h2>
              <p className="text-teal-200 mt-1">Your content is powerful. But only if it gets deployed.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mb-3">
                  <CalendarDaysIcon className="h-6 w-6 text-copper-300" />
                </div>
                <h3 className="font-bold text-cream-100 mb-1">Set It & Forget It</h3>
                <p className="text-sm text-teal-200 leading-relaxed">Blog posts publish weekly. Social content goes out daily. Emails trigger automatically. You focus on your business.</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mb-3">
                  <ChartBarIcon className="h-6 w-6 text-copper-300" />
                </div>
                <h3 className="font-bold text-cream-100 mb-1">Data-Driven Optimization</h3>
                <p className="text-sm text-teal-200 leading-relaxed">We track what's working and double down. Underperforming content gets refreshed. Your marketing gets smarter every month.</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mb-3">
                  <ArrowPathIcon className="h-6 w-6 text-copper-300" />
                </div>
                <h3 className="font-bold text-cream-100 mb-1">Fresh Content Monthly</h3>
                <p className="text-sm text-teal-200 leading-relaxed">After your initial content runs, we generate new seasonal campaigns, holiday promotions, and trending topics.</p>
              </div>
            </div>
          </div>

          {/* ── Comparison Table ── */}
          <div className="bg-white rounded-2xl border border-charcoal-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-charcoal-100">
              <h2 className="text-xl font-bold text-charcoal-700">Compare Your Options</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-charcoal-50">
                    <th className="text-left px-6 py-3 text-charcoal-500 font-medium">Feature</th>
                    <th className="text-center px-6 py-3 text-charcoal-500 font-medium">Export</th>
                    <th className="text-center px-6 py-3 text-copper-600 font-bold">Evergreen</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'All generated content', export: true, evergreen: true },
                    { feature: 'Website hosting & SSL', export: false, evergreen: true },
                    { feature: 'Auto-publish blog posts', export: false, evergreen: true },
                    { feature: 'Auto-publish social media', export: false, evergreen: true },
                    { feature: 'Email & SMS automation', export: false, evergreen: true },
                    { feature: 'Phone & Texting AI', export: false, evergreen: true },
                    { feature: 'Paid ad campaigns', export: false, evergreen: true },
                    { feature: 'Monthly content refresh', export: false, evergreen: true },
                    { feature: 'SEO monitoring & updates', export: false, evergreen: true },
                    { feature: 'Performance analytics', export: false, evergreen: true },
                    { feature: 'AI-powered optimization', export: false, evergreen: true },
                    { feature: 'Priority support', export: false, evergreen: true },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-charcoal-50/50'}>
                      <td className="px-6 py-3 text-charcoal-600">{row.feature}</td>
                      <td className="text-center px-6 py-3">
                        {row.export ? (
                          <CheckCircleIcon className="h-5 w-5 text-sage-500 inline" />
                        ) : (
                          <span className="text-charcoal-300">—</span>
                        )}
                      </td>
                      <td className="text-center px-6 py-3">
                        <CheckCircleIcon className="h-5 w-5 text-copper-500 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Not ready yet */}
      {!allComplete && (
        <div className="bg-cream-50 border border-copper-200 rounded-2xl p-6 text-center">
          <p className="text-charcoal-500 font-medium">
            Complete the remaining {checklist.length - completedCount} phase{checklist.length - completedCount !== 1 ? 's' : ''} to unlock activation options.
          </p>
        </div>
      )}
    </div>
  );
}
